"use client";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
  Label,
} from "recharts";
import { useEffect, useState } from "react";

const rawData = [
  { year: 2020, demand: 1100, demandForecast: null, losses: 17 },
  { year: 2022.5, demand: 1800, demandForecast: null, losses: 16 },
  { year: 2025, demand: 2316, demandForecast: 2316, losses: 15.5 },
  { year: 2027.5, demand: null, demandForecast: 2950, losses: 16 },
  { year: 2030, demand: null, demandForecast: 3550, losses: 16.5 },
  { year: 2034, demand: null, demandForecast: 4350, losses: 16 },
  { year: 2038, demand: null, demandForecast: 5100, losses: 15.2 },
  { year: 2040, demand: null, demandForecast: null, losses: 15.2 },
];

const renderDemandDot = (props: any) => {
  const { cx, cy, payload } = props;
  if (!cx || !cy) return null;
  if (payload.year === 2025) {
    return (
      <circle
        cx={cx}
        cy={cy}
        r={7}
        fill="#003350"
        stroke="none"
        key={`dot-${payload.year}`}
      />
    );
  }
  return (
    <circle
      cx={cx}
      cy={cy}
      r={4.5}
      fill="#003350"
      stroke="none"
      key={`dot-${payload.year}`}
    />
  );
};

const renderLossesDot = (props: any) => {
  const { cx, cy, payload } = props;
  if (!cx || !cy) return null;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={4.5}
      fill="#48DBB2"
      stroke="none"
      key={`dot-${payload.year}`}
    />
  );
};

const CustomLabel1 = (props: any) => {
  const { x, y } = props.viewBox;
  return (
    <text x={x} y={y} fill="#003350" textAnchor="middle" className="text-sm">
      <tspan x={x} dy="-2.5em" fontWeight="bold" fontSize="16">
        2,316 MW
      </tspan>
      <tspan x={x} dy="1.2em" fontSize="14">
        (2025)
      </tspan>
    </text>
  );
};

const CustomLabel2 = (props: any) => {
  const { x, y } = props.viewBox;
  return (
    <text
      x={x}
      y={y}
      fill="#003350"
      textAnchor="middle"
      className="text-[13px]"
    >
      <tspan x={x} dy="-2em">
        Demand could
      </tspan>
      <tspan x={x} dy="1.2em">
        more than
      </tspan>
      <tspan x={x} dy="1.2em">
        double by 2040
      </tspan>
    </text>
  );
};

const CustomLabel3 = (props: any) => {
  const { x, y } = props.viewBox;
  return (
    <text x={x} y={y} fill="#48DBB2" textAnchor="middle">
      <tspan x={x} dy="-0.2em" fontWeight="bold" fontSize="20">
        ~20%
      </tspan>
      <tspan x={x} dy="1.2em" fontSize="14">
        losses in 2025
      </tspan>
    </text>
  );
};

export default function DualLinesChart({ isActive }: { isActive: boolean }) {
  const [data, setData] = useState<any[]>([]);
  const [animationKey, setAnimationKey] = useState(0);

  useEffect(() => {
    if (isActive) {
      setTimeout(() => {
        setData(rawData);
        setAnimationKey((prev) => prev + 1);
      }, 200);
    } else {
      setData([]);
    }
  }, [isActive]);

  return (
    <div className="w-full h-full flex justify-center items-center bg-[#f9f9f9] p-4 font-sans relative">
      <div className="w-full max-w-[900px] h-[450px] relative mt-5">
        {/* Absolute positioned titles */}
        <div className="absolute top-0 left-0 text-[#003350] font-semibold z-10">
          Peak Demand (MW)
        </div>
        <div className="absolute top-0 right-0 text-[#48DBB2] font-semibold z-10">
          System Losses (%)
        </div>

        {/* Legend */}
        <div className="absolute top-12 left-24 flex flex-col gap-3 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-0 border-t-[2.5px] border-[#003350] relative">
              <div className="w-[10px] h-[10px] bg-[#003350] rounded-full absolute -top-[6.5px] left-1/2 -translate-x-1/2"></div>
            </div>
            <span className="text-gray-700 text-[15px]">Peak Demand (MW)</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-0 border-t-[2.5px] border-[#48DBB2] relative">
              <div className="w-[10px] h-[10px] bg-[#48DBB2] rounded-full absolute -top-[6.5px] left-1/2 -translate-x-1/2"></div>
            </div>
            <span className="text-gray-700 text-[15px]">System Losses (%)</span>
          </div>
        </div>

        <div className="w-full h-[400px] mt-10">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              key={animationKey}
              margin={{ top: 20, right: 10, left: 10, bottom: 20 }}
            >
              <CartesianGrid vertical={false} stroke="#e5e7eb" />
              <XAxis
                dataKey="year"
                type="number"
                domain={[2020, 2040]}
                ticks={[2020, 2025, 2030, 2035, 2040]}
                axisLine={{ stroke: "#9ca3af" }}
                tickLine={true}
                tick={{ fill: "#4b5563", fontSize: 14 }}
                tickMargin={10}
              />
              <YAxis
                yAxisId="left"
                domain={[0, 5000]}
                ticks={[0, 1000, 2000, 3000, 4000, 5000]}
                axisLine={{ stroke: "#9ca3af" }}
                tickLine={true}
                tick={{ fill: "#4b5563", fontSize: 14 }}
                tickFormatter={(val) => val.toLocaleString()}
                tickMargin={10}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, 30]}
                ticks={[0, 5, 10, 15, 20, 25, 30]}
                axisLine={{ stroke: "#9ca3af" }}
                tickLine={true}
                tick={{ fill: "#4b5563", fontSize: 14 }}
                tickFormatter={(val) => `${val}%`}
                tickMargin={10}
              />
              <Tooltip
                formatter={(value: any, name: any) => {
                  if (name === "losses") return [`${value}%`, "System Losses"];
                  return [`${value ? Number(value).toLocaleString() : ""} MW`, "Peak Demand"];
                }}
                labelFormatter={(label) => `Year: ${label}`}
              />

              <Line
                yAxisId="left"
                type="monotone"
                dataKey="demand"
                stroke="#003350"
                strokeWidth={2.5}
                dot={renderDemandDot}
                activeDot={{ r: 8 }}
                isAnimationActive={true}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="demandForecast"
                stroke="#003350"
                strokeWidth={2.5}
                strokeDasharray="6 6"
                dot={renderDemandDot}
                activeDot={{ r: 8 }}
                isAnimationActive={true}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="losses"
                stroke="#48DBB2"
                strokeWidth={2.5}
                dot={renderLossesDot}
                activeDot={{ r: 8 }}
                isAnimationActive={true}
              />

              {/* Annotations using ReferenceDot */}
              <ReferenceDot
                yAxisId="left"
                x={2025}
                y={2400}
                r={0}
                stroke="none"
              >
                <Label content={<CustomLabel1 />} />
              </ReferenceDot>

              <ReferenceDot
                yAxisId="left"
                x={2036.5}
                y={3500}
                r={0}
                stroke="none"
              >
                <Label content={<CustomLabel2 />} />
              </ReferenceDot>

              <ReferenceDot
                yAxisId="right"
                x={2037.8}
                y={10}
                r={0}
                stroke="none"
              >
                <Label content={<CustomLabel3 />} />
              </ReferenceDot>
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
